import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type { JourneyTaskCreateInput } from "./types";

export type AddTaskInput = {
  journeyId: string;
  input: JourneyTaskCreateInput;
};

export type AddTaskResponse = { id: string };

// POST /api/admin/journeys/:journeyId/tasks
//
// Replaces /app/admin/journeys/_actions.ts::createJourneyTaskAction.
// Pessimistic — journey_tasks.id is a Postgres-generated uuid and the row
// carries many server-defaulted columns (position, assignee_kind, visibility,
// due_offset_days, action, timestamps). Synthesizing all of those locally
// for an optimistic patch is brittle compared to a brief refetch.
// (Bucket 5's use-add-field is optimistic because form fields are stored in
// a JSONB blob with SPA-generated UUIDs; the journey-task table has no such
// mechanism.)
export function useAddTask() {
  const queryClient = useQueryClient();
  return useMutation<AddTaskResponse, ApiClientError, AddTaskInput>({
    mutationFn: ({ journeyId, input }) =>
      apiFetch<AddTaskResponse>(`/admin/journeys/${journeyId}/tasks`, {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: (_data, { journeyId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.journeys.detail(journeyId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.portal.journeys.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.portal.tasks.all });
    },
    onError: (err) => {
      toast.error(err.message ?? "Failed to add task");
    },
  });
}
